import React, { useRef } from 'react';
import { ArcIconButton, ArcStack, ArcText } from '@src/core/ui';
import { DroneCardProps, TopDronesCarouselProps } from './types';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { DroneEntity, DroneStatusType } from '@src/core/features';
import { setMissionId, setSelectedDrone } from '@src/features/drone/redux/actions';
import { arcToFixed } from '@src/core/extensions';
import { ReactComponent as ArrowLeftIcon } from '@src/assets/icons/arrow-left.svg';
import { setMapLocation } from '@src/core/controllers';
import { DRONE_CARD_STYLES, CAROUSEL_STYLES, POPUP_BUTTON_STYLES, ArrowBackIosTight } from './styles';

const MAX_VISIBLE_DRONES = 3;

const formatDroneStatus = (drone: DroneEntity, getMissionProgress: (drone: DroneEntity) => number | null): string => {
  if (drone.status === DroneStatusType.MISSION) {
    const progress = getMissionProgress(drone);
    if (progress !== null) {
      return `MISSION (${parseFloat(arcToFixed(progress, 1))}%)`;
    }
  }
  return drone.status;
};

const DroneCard: React.FC<DroneCardProps> = React.memo(({ drone, isSelected, onClick, getMissionProgress, maxWidth = '200px' }) => (
  <ArcStack
    borderColor={isSelected ? 'brand.main' : 'transparent'}
    onClick={onClick}
    bgcolor="background.paper"
    sx={{ ...DRONE_CARD_STYLES, maxWidth }}
  >
    <ArcText.caption
      fontSize={12}
      whiteSpace="nowrap"
      fontWeight={400}
      color="white"
      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {drone.name}
    </ArcText.caption>
    <ArcText.caption
      fontSize={10}
      whiteSpace="nowrap"
      fontWeight={500}
      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {formatDroneStatus(drone, getMissionProgress)}
    </ArcText.caption>
  </ArcStack>
));

DroneCard.displayName = 'DroneCard';

export const TopDronesCarousel: React.FC<TopDronesCarouselProps> = ({ idleDrones, flyingDrones, selectedDrone, flightData }) => {
  const [, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const carouselRef = useRef<HTMLDivElement>(null);
  const carouselPositionRef = React.useRef<number | null>(null);
  
  const [previousMissionProgress, setPreviousMissionProgress] = React.useState<Record<string, number>>({});
  const [showRemainingPopup, setShowRemainingPopup] = React.useState(false);
  const popupContainerRef = React.useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    if (flightData?.serialNumber && typeof flightData?.missionProgress === 'number') {
      const serial = flightData.serialNumber;
      const newProgress: number = flightData.missionProgress;
      setPreviousMissionProgress((prev) => {
        if (prev[serial] === newProgress) return prev;
        return { ...prev, [serial]: newProgress };
      });
    }
  }, [flightData]);

  const isSelected = (drone: DroneEntity) => selectedDrone?.id === drone.id;
  
  const getMissionProgress = React.useCallback((drone: DroneEntity): number | null => {
    if (flightData?.serialNumber === drone.serialNumber && typeof flightData?.missionProgress === 'number') {
      return flightData.missionProgress;
    }
    return previousMissionProgress[drone.serialNumber] ?? null;
  }, [flightData, previousMissionProgress]);

  const sortedDrones = React.useMemo(() => {
    // If no selected drone, mission drones first, then idle
    if (!selectedDrone) {
      return [...flyingDrones, ...idleDrones];
    }

    const selectedId = selectedDrone.id;
    if (carouselPositionRef.current !== null) {
      const allDrones = [...flyingDrones, ...idleDrones];
      const currentIndex = allDrones.findIndex(d => d.id === selectedId);
      if (currentIndex !== -1) {
        const result = [...allDrones];
        result.splice(currentIndex, 1);
        result.splice(carouselPositionRef.current, 0, selectedDrone);
        return result;
      }
      carouselPositionRef.current = null;
    }
    
    // Selected drone always first, then mission drones, then idle drones
    const idleWithoutSelected = idleDrones.filter(d => d.id !== selectedId);
    const missionWithoutSelected = flyingDrones.filter(d => d.id !== selectedId);
    
    return [selectedDrone, ...missionWithoutSelected, ...idleWithoutSelected];
  }, [idleDrones, flyingDrones, selectedDrone]);

  const visibleDrones = React.useMemo(() => {
    return sortedDrones.slice(0, MAX_VISIBLE_DRONES);
  }, [sortedDrones]);

  const remainingDrones = React.useMemo(() => {
    return sortedDrones.slice(MAX_VISIBLE_DRONES);
  }, [sortedDrones]);

  const remainingCount = remainingDrones.length;

 

  const handleDroneSelect = React.useCallback((drone: DroneEntity, index: number) => {
    carouselPositionRef.current = index >= MAX_VISIBLE_DRONES ? MAX_VISIBLE_DRONES - 1 : index;
    dispatch(setMissionId(undefined));
    dispatch(setMapLocation({
      latitude: Number(drone.latitude),
      longitude: Number(drone.longitude),
      address: drone.name,
    }));
    setSearchParams({ droneSerial: drone.serialNumber });
    setShowRemainingPopup(false);
  }, [dispatch, setSearchParams]);

  const onGoBack = () => {
    setSearchParams();
    dispatch(setSelectedDrone(null));
  };

  return (
    <ArcStack 
      direction="row" 
      spacing={1.2}
      alignItems="center"
    >
      <ArcStack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
        <ArcIconButton onClick={onGoBack}><ArrowLeftIcon width={18}/></ArcIconButton>
      </ArcStack>
      <ArcStack
        ref={carouselRef}
        direction="row"
        spacing={1.2}
        alignItems="center"
        sx={{
          ...CAROUSEL_STYLES,
          overflowX: 'auto',
          overflowY: 'visible',
        }}
        className="no-scrollbar"
      >
        {visibleDrones.map((drone) => (
          <DroneCard
            key={drone.id}
            drone={drone}
            isSelected={isSelected(drone)}
            onClick={() => handleDroneSelect(drone, sortedDrones.findIndex(d => d.id === drone.id))}
            getMissionProgress={getMissionProgress}
          />
        ))}
        {remainingCount > 0 && (
          <ArcStack
            borderColor="transparent"
            onClick={() => setShowRemainingPopup(true)}
            bgcolor="background.paper"
            sx={{ 
              ...POPUP_BUTTON_STYLES,
              minWidth: 'max-content',
              flexShrink: 0,
              '&:hover': { bgcolor: 'grey.800' },
            }}
          >
            {showRemainingPopup ? (
              <ArrowBackIosTight sx={{ color: 'text.secondary', fontSize: 15 }} />
            ) : (
              <ArcText.caption fontSize={12} whiteSpace="nowrap" fontWeight={400} color="white" minWidth={'15px'}>
                +{remainingCount}
              </ArcText.caption>
            )}
          </ArcStack>
        )}
      </ArcStack>

      {showRemainingPopup && remainingCount > 0 && (
        <>
          <ArcStack
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={99}
            sx={{ cursor: 'pointer' }}
            onClick={() => setShowRemainingPopup(false)}
          />
          <ArcStack
            p="10px"
            width="100%"
            maxWidth="fit-content"
            borderRadius={1}
            position="absolute"
            left={480}
            top={53}
            sx={{ maxHeight: '70vh', overflowY: 'auto' }}
            zIndex={100}
            bgcolor="background.default"
          >
            <ArcStack ref={popupContainerRef} spacing={1.2}>
              {remainingDrones.map((drone) => (
                <DroneCard
                  key={drone.id}
                  drone={drone}
                  isSelected={isSelected(drone)}
                  onClick={() => handleDroneSelect(drone, sortedDrones.findIndex(d => d.id === drone.id))}
                  getMissionProgress={getMissionProgress}
                />
              ))}
            </ArcStack>
          </ArcStack>
        </>
      )}
    </ArcStack>
  );
};
